/**
 * Anthropic Messages API streaming, shared by every host that talks to Claude
 * through `/api/ai/anthropic/messages`.
 *
 * The turn is spread across SSE frames rather than delivered in a single
 * terminal envelope, so content blocks are reassembled per `index` and handed
 * back verbatim: thinking blocks keep their signatures because a tool-use loop
 * must replay them unmodified on the next request.
 */

import { isAbortError } from "../utils/abortError";
import { isTimeoutFetchError, isTransientFetchError, readModelError } from "../utils/http";

type AnthropicContentBlock = Record<string, unknown>;

/** A single tool invocation requested by the model. */
export interface AnthropicToolCall {
  callId: string;
  name: string;
  /** Raw JSON argument string, exactly as streamed by the model. */
  arguments: string;
}

/** One reassembled Messages turn. */
export interface AnthropicTurnResult {
  /** Content blocks in stream order, replayed as-is on the next request. */
  raw: unknown[];
  toolCalls: AnthropicToolCall[];
  text: string;
  reasoning: string;
  stopReason: string;
}

/**
 * Which `stop_reason` values end the turn with an `AnthropicStreamError`
 * instead of returning it. Excel treats a truncated or refused turn as fatal;
 * a caller that handles truncation itself passes only `refusal`.
 */
export type AnthropicStopReasonPolicy = readonly string[];

export const DEFAULT_REJECTED_STOP_REASONS: AnthropicStopReasonPolicy = ["max_tokens", "refusal"];

export interface ReadAnthropicStreamOptions {
  /** Receives the running concatenation of assistant text. */
  onText?: (partial: string) => void;
  /** Receives the running concatenation of reasoning text. */
  onReasoning?: (partial: string) => void;
  /** Receives every parsed SSE payload before it is folded into the turn. */
  onEvent?: (event: Record<string, unknown>) => void;
  rejectStopReasons?: AnthropicStopReasonPolicy;
}

export interface AnthropicStreamTurnParams extends ReadAnthropicStreamOptions {
  /** Performs one HTTP attempt. Retried on transient failures. */
  request: () => Promise<Response>;
  /** Label used in error messages. */
  contextLabel: string;
  signal?: AbortSignal;
  /** Body-level replay budget for transient stream failures. */
  maxBodyRetries?: number;
  onRetry?: (error: unknown) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function blockType(block: unknown): string {
  return isRecord(block) && typeof block.type === "string" ? block.type : "";
}

export class AnthropicStreamError extends Error {
  code: string;
  transient: boolean;
  details?: unknown;

  constructor(message: string, options: { code: string; transient?: boolean; details?: unknown }) {
    super(message);
    this.name = "AnthropicStreamError";
    this.code = options.code;
    this.transient = options.transient === true;
    this.details = options.details;
  }
}

/**
 * Anthropic error envelopes that are worth one replay. `invalid_request_error`
 * and the auth/permission family are deterministic and must surface instead.
 */
const TRANSIENT_ANTHROPIC_ERROR_TYPES = new Set([
  "api_error",
  "overloaded_error",
  "rate_limit_error",
  "timeout_error",
]);

/**
 * The proxy performs no retry of its own, so upstream 429/5xx reach the client
 * with their original status and are replayed here instead.
 */
export const TRANSIENT_HTTP_STATUSES = new Set([408, 409, 429, 500, 502, 503, 504, 529]);

/** Upper bound on honouring a `retry-after` before a replay. */
export const MAX_RETRY_AFTER_MS = 5000;

export function isTransientAnthropicStreamError(error: unknown): boolean {
  return (
    (error instanceof AnthropicStreamError && error.transient) ||
    isTimeoutFetchError(error) ||
    isTransientFetchError(error)
  );
}

export function parseRetryAfterMs(response: Response): number {
  const header = response.headers?.get?.("retry-after");
  if (!header) return 0;
  const seconds = Number.parseFloat(header);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/** One content block being assembled from its `content_block_*` frames. */
interface StreamingBlock {
  start: Record<string, unknown>;
  type: string;
  text: string;
  thinking: string;
  signature: string;
  partialJson: string;
}

function toRawBlock(block: StreamingBlock): AnthropicContentBlock {
  const raw: AnthropicContentBlock = { ...block.start, type: block.type };
  if (block.type === "tool_use") {
    raw.input = safeParseToolInput(block.partialJson, block.start.input);
    return raw;
  }
  if (block.type === "thinking") {
    raw.thinking = block.thinking;
    // Unsigned thinking is rejected on replay, so only attach a real signature.
    if (block.signature) raw.signature = block.signature;
    return raw;
  }
  if (block.type === "redacted_thinking") return raw;
  if (block.type === "text") {
    raw.text = block.text;
    return raw;
  }
  // Unknown block types (e.g. future server-tool blocks) round-trip as sent.
  return raw;
}

function safeParseToolInput(partialJson: string, fallback: unknown): unknown {
  if (!partialJson.trim()) return isRecord(fallback) ? fallback : {};
  try {
    return JSON.parse(partialJson);
  } catch {
    // Kept verbatim on the call's argument string: the caller re-parses it and
    // reports the malformed payload back to the model rather than failing.
    return isRecord(fallback) ? fallback : {};
  }
}

/**
 * Reads one chunk, reporting a mid-stream transport failure as a typed stream
 * error rather than whatever the platform threw. A caller abort is rethrown
 * untouched so it keeps stopping the run immediately.
 */
async function readStreamChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>
): Promise<ReadableStreamReadResult<Uint8Array>> {
  try {
    return await reader.read();
  } catch (error) {
    if (isAbortError(error) || error instanceof AnthropicStreamError) throw error;
    throw new AnthropicStreamError(
      `Anthropic streaming response failed mid-stream: ${
        error instanceof Error ? error.message : String(error)
      }`,
      {
        code: "stream_read_failed",
        transient: isTransientFetchError(error),
        details: error,
      }
    );
  }
}

/**
 * Consumes one Messages API SSE stream.
 *
 *  - `message_start`      — message shell and input usage
 *  - `content_block_start`— the block's type plus `tool_use` id/name
 *  - `content_block_delta`— `text_delta`, `thinking_delta`, `signature_delta`,
 *                           and `input_json_delta` fragments per block index
 *  - `content_block_stop` — block complete
 *  - `message_delta`      — `stop_reason` and output usage
 *  - `message_stop`       — end of turn; its absence means a truncated stream
 *
 * `signature_delta` is retained (unlike the server's observability tee, which
 * drops it) because an unsigned thinking block cannot be replayed.
 */
export async function readAnthropicMessagesStream(
  response: Response,
  options: ReadAnthropicStreamOptions = {}
): Promise<AnthropicTurnResult> {
  const { onText, onReasoning, onEvent } = options;
  const rejectStopReasons = options.rejectStopReasons ?? DEFAULT_REJECTED_STOP_REASONS;
  const reader = response.body?.getReader();
  if (!reader) {
    throw new AnthropicStreamError("Anthropic streaming response did not include a readable body", {
      code: "stream_missing_body",
      transient: false,
    });
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let textContent = "";
  let reasoningContent = "";
  let stopReason = "";
  let sawMessageStop = false;
  const blocks = new Map<number, StreamingBlock>();
  const blockIndices: number[] = [];

  const ensureBlock = (index: unknown, fallbackType: string): StreamingBlock => {
    const key = typeof index === "number" && Number.isInteger(index) ? index : -1;
    let block = blocks.get(key);
    if (!block) {
      blockIndices.push(key);
      block = {
        start: {},
        type: fallbackType || "text",
        text: "",
        thinking: "",
        signature: "",
        partialJson: "",
      };
      blocks.set(key, block);
    }
    return block;
  };

  const handleEvent = (eventText: string): void => {
    const dataLines = eventText
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).replace(/^\s+/, ""));
    if (dataLines.length === 0) return;
    const dataText = dataLines.join("\n");
    if (!dataText || dataText === "[DONE]") return;

    let event: unknown;
    try {
      event = JSON.parse(dataText);
    } catch {
      return;
    }
    if (!isRecord(event)) return;
    if (onEvent) {
      try {
        onEvent(event);
      } catch {
        // An observer error must never abort the model stream.
      }
    }

    switch (event.type) {
      case "content_block_start": {
        const start = isRecord(event.content_block) ? event.content_block : {};
        const block = ensureBlock(event.index, blockType(start));
        block.start = { ...start };
        if (typeof start.type === "string") block.type = start.type;
        if (typeof start.text === "string" && start.text) {
          block.text += start.text;
          textContent += start.text;
          onText?.(textContent);
        }
        if (typeof start.thinking === "string" && start.thinking) {
          block.thinking += start.thinking;
          reasoningContent += start.thinking;
          onReasoning?.(reasoningContent);
        }
        if (typeof start.signature === "string") block.signature = start.signature;
        return;
      }
      case "content_block_delta": {
        const delta = isRecord(event.delta) ? event.delta : {};
        if (delta.type === "text_delta" && typeof delta.text === "string") {
          const block = ensureBlock(event.index, "text");
          block.text += delta.text;
          textContent += delta.text;
          onText?.(textContent);
          return;
        }
        if (delta.type === "thinking_delta" && typeof delta.thinking === "string") {
          const block = ensureBlock(event.index, "thinking");
          block.thinking += delta.thinking;
          reasoningContent += delta.thinking;
          onReasoning?.(reasoningContent);
          return;
        }
        if (delta.type === "signature_delta" && typeof delta.signature === "string") {
          ensureBlock(event.index, "thinking").signature += delta.signature;
          return;
        }
        if (delta.type === "input_json_delta" && typeof delta.partial_json === "string") {
          ensureBlock(event.index, "tool_use").partialJson += delta.partial_json;
        }
        return;
      }
      case "message_delta": {
        const delta = isRecord(event.delta) ? event.delta : {};
        if (typeof delta.stop_reason === "string") stopReason = delta.stop_reason;
        return;
      }
      case "message_stop": {
        sawMessageStop = true;
        return;
      }
      case "error": {
        const details = isRecord(event.error) ? event.error : event;
        const code = typeof details.type === "string" ? details.type : "anthropic_error";
        throw new AnthropicStreamError(
          typeof details.message === "string" && details.message.trim()
            ? details.message.trim()
            : "Anthropic streaming response failed",
          {
            code,
            transient: TRANSIENT_ANTHROPIC_ERROR_TYPES.has(code),
            details,
          }
        );
      }
      default:
        // `message_start`, `content_block_stop`, and `ping` need no bookkeeping;
        // the turn is reassembled from the accumulated blocks below.
        return;
    }
  };

  try {
    while (true) {
      const { value, done } = await readStreamChunk(reader);
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      for (const eventText of events) {
        handleEvent(eventText);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) handleEvent(buffer);
  } catch (error) {
    try {
      await reader.cancel();
    } catch {
      // Best-effort cleanup before surfacing the stream error.
    }
    throw error;
  } finally {
    reader.releaseLock();
  }

  if (!sawMessageStop) {
    // The proxy emits no error frame when an upstream stream breaks after the
    // headers, so a missing `message_stop` is the only truncation signal.
    throw new AnthropicStreamError("Anthropic streaming response ended before message_stop", {
      code: "stream_ended_before_completion",
      transient: true,
    });
  }
  if (rejectStopReasons.includes(stopReason)) {
    throw new AnthropicStreamError(`Anthropic response ended with stop_reason ${stopReason}`, {
      code: `stop_${stopReason}`,
      transient: false,
    });
  }

  const ordered = blockIndices
    .slice()
    .sort((left, right) => left - right)
    .map((index) => blocks.get(index) as StreamingBlock);
  const raw = ordered.map(toRawBlock);
  const toolCalls: AnthropicToolCall[] = ordered
    .filter((block) => block.type === "tool_use")
    .map((block) => ({
      callId: String(block.start.id ?? ""),
      name: String(block.start.name ?? ""),
      arguments: block.partialJson.trim()
        ? block.partialJson
        : JSON.stringify(isRecord(block.start.input) ? block.start.input : {}),
    }));
  // A nameless call cannot be dispatched and an id-less one cannot be answered
  // with a paired `tool_result`, so the turn is unusable rather than degraded.
  if (toolCalls.some((call) => !call.callId || !call.name)) {
    throw new AnthropicStreamError("Anthropic returned a tool_use block without an id or a name", {
      code: "malformed_tool_use",
      transient: false,
    });
  }

  return { raw, toolCalls, text: textContent, reasoning: reasoningContent, stopReason };
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Forwards thinking text to the caller as it streams, at most once.
 *
 * A replayed attempt re-streams the turn from its first token, so its running
 * text walks back over ground the discarded attempt already narrated. Only
 * text that continues the high-water mark actually handed to the caller is
 * forwarded: a replay of the same reasoning stays silent, and a replay that
 * thinks further narrates just the part that is new.
 */
export function createReasoningRelay(
  onReasoning: ((partial: string) => void) | undefined
): ((partial: string) => void) | undefined {
  if (!onReasoning) return undefined;
  let narrated = "";
  return (partial: string): void => {
    if (partial.length <= narrated.length || !partial.startsWith(narrated)) return;
    narrated = partial;
    onReasoning(partial);
  };
}

/**
 * Fetches and consumes one Messages turn, replaying only transient failures.
 * Reasoning streams out as it arrives; the relay guarantees a discarded
 * attempt cannot narrate twice.
 */
export async function streamAnthropicTurn(
  params: AnthropicStreamTurnParams
): Promise<AnthropicTurnResult> {
  const maxBodyRetries = Math.max(0, params.maxBodyRetries ?? 1);
  const relayReasoning = createReasoningRelay(params.onReasoning);
  for (let attempt = 0; attempt <= maxBodyRetries; attempt += 1) {
    if (params.signal?.aborted) {
      const error = new Error("The operation was aborted");
      error.name = "AbortError";
      throw error;
    }
    try {
      const response = await params.request();
      if (!response.ok) {
        throw new AnthropicStreamError(await readModelError(response, params.contextLabel), {
          code: `http_${response.status}`,
          transient: TRANSIENT_HTTP_STATUSES.has(response.status),
          details: { retryAfterMs: parseRetryAfterMs(response) },
        });
      }
      return await readAnthropicMessagesStream(response, {
        onText: params.onText,
        onReasoning: relayReasoning,
        onEvent: params.onEvent,
        rejectStopReasons: params.rejectStopReasons,
      });
    } catch (error) {
      if (
        params.signal?.aborted ||
        isAbortError(error) ||
        !isTransientAnthropicStreamError(error) ||
        attempt >= maxBodyRetries
      ) {
        throw error;
      }
      params.onRetry?.(error);
      const retryAfterMs =
        error instanceof AnthropicStreamError && isRecord(error.details)
          ? Number(error.details.retryAfterMs) || 0
          : 0;
      if (retryAfterMs > 0) await delay(retryAfterMs);
    }
  }
  throw new Error(`${params.contextLabel} stream retry exhausted`);
}
